import { BOARD_COLUMNS, columnOf, moveTarget, stageTitle, type Stage } from '@jt/core';
import type { ApplicationListItem } from '@jt/shared';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type CSSProperties } from 'react';
import { keys, useAppActions } from '../api/hooks';
import { AppCard } from './AppCard';
import { openNewApplication } from './NewApplication';
import { useUI } from './overlays';
import { Icon } from './ui';

/** Колонка под курсором или пальцем; если указатель вне колонок — по пересечению карточки. */
const collision: CollisionDetection = (args) => {
  const hit = pointerWithin(args);
  return hit.length ? hit : rectIntersection(args);
};

function DraggableCard({ a, ai, selected }: { a: ApplicationListItem; ai: boolean; selected: boolean }) {
  const { setNodeRef, listeners, isDragging } = useDraggable({ id: a.id, data: { app: a } });
  return (
    <AppCard
      a={a}
      ai={ai}
      selected={selected}
      dragRef={setNodeRef}
      className={isDragging ? 'ghost' : ''}
      aria-roledescription="карточка, можно перетащить"
      {...listeners}
    />
  );
}

function Column({
  col,
  items,
  ai,
  selectedId,
}: {
  col: (typeof BOARD_COLUMNS)[number];
  items: ApplicationListItem[];
  ai: boolean;
  selectedId: string | null;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: col.key, data: { stage: col.stage } });
  const ui = useUI();
  return (
    <section
      className={`col ${isOver ? 'dropover' : ''}`}
      ref={setNodeRef}
      data-col={col.key}
      aria-label={col.title}
    >
      <header className="colh">
        <span className="cic" style={{ '--c': `var(--s-${col.stage})` } as CSSProperties}>
          <Icon name={col.icon} size={20} />
        </span>
        <b title={stageTitle(col.stage)}>{col.title}</b>
        <span className="cnt">· {items.length}</span>
        <button
          className="icon-btn"
          aria-label="Действия с колонкой"
          onClick={(e) =>
            ui.openMenu(
              e.currentTarget,
              <button role="menuitem" onClick={() => openNewApplication(ui, { stage: col.stage })}>
                <Icon name="plus" size={16} /> Добавить вакансию сюда
              </button>,
            )
          }
        >
          <Icon name="more" />
        </button>
      </header>
      <div className="cards">
        {items.length ? (
          items.map((a) => <DraggableCard key={a.id} a={a} ai={ai} selected={selectedId === a.id} />)
        ) : (
          <div className="colempty">Пусто</div>
        )}
      </div>
    </section>
  );
}

/** Доска по этапам: перетаскивание мышью и пальцем (долгое нажатие), оптимистичное обновление с откатом. */
export function Board({
  list,
  ai,
  selectedId,
}: {
  list: ApplicationListItem[];
  ai: boolean;
  selectedId: string | null;
}) {
  const qc = useQueryClient();
  const act = useAppActions();
  const ui = useUI();
  const [dragging, setDragging] = useState<ApplicationListItem | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );
  const cols = BOARD_COLUMNS.filter((c) => c.key !== 'saved' || list.some((a) => a.stage === 'saved'));

  const onEnd = (e: DragEndEvent) => {
    setDragging(null);
    const a = e.active.data.current?.app as ApplicationListItem | undefined;
    const stage = e.over?.data.current?.stage as Stage | undefined;
    if (!a || !stage) return;
    const to = moveTarget(a.stage, stage);
    if (!to) return;
    const snapshot = qc.getQueriesData<ApplicationListItem[]>({ queryKey: keys.apps() });
    qc.setQueriesData<ApplicationListItem[]>({ queryKey: keys.apps() }, (l) =>
      l?.map((x) => (x.id === a.id ? { ...x, stage: to } : x)),
    );
    act.stage(a.id, to).then(
      () => ui.toast(`Перенесено: ${stageTitle(to)}`),
      () => {
        for (const [k, v] of snapshot) qc.setQueryData(k, v);
        ui.toast('Не получилось перенести — карточка вернулась на место.');
      },
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collision}
      onDragStart={(e: DragStartEvent) =>
        setDragging((e.active.data.current?.app as ApplicationListItem) ?? null)
      }
      onDragEnd={onEnd}
      onDragCancel={() => setDragging(null)}
      // мягкая автопрокрутка у края: на телефоне доска листается по колонке, не проскакивая цель
      autoScroll={{ threshold: { x: 0.12, y: 0.12 }, acceleration: 3 }}
    >
      <div className={`board ${dragging ? 'dragging' : ''}`} id="board">
        {cols.map((c) => (
          <Column
            key={c.key}
            col={c}
            items={list.filter((a) => columnOf(a.stage) === c.key)}
            ai={ai}
            selectedId={selectedId}
          />
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {dragging ? <AppCard a={dragging} ai={ai} className="overlay" /> : null}
      </DragOverlay>
    </DndContext>
  );
}
